/*****************************************************************************
 *
 * String constants used throughout the application. Changing text here will
 * alter the displayed text in the application when rebuilt.
 *
 *****************************************************************************/

export const TEXT = {
  page_header: "Economic Cost of Opioid Use Disorder and Fatal Overdose, 2017",

  state_title_total: "Economic Cost by State: Total Costs",
  state_title_nonfatal: "Economic Cost by State: Opioid Use Disorder Costs",
  state_title_fatal: "Economic Cost by State: Fatal Overdose Costs",
  state_description: "The dashboard presents the economic cost of opioid use disorder and fatal overdose at the state level in 2017. Estimates of the economic cost can help decision makers, clinicians, public health professionals, researchers, and other stakeholders better understand the magnitude and distribution of opioid use disorder and fatal overdose costs in individual states of the U.S.",
  state_info_title: "Data Sources and Other Information",
  state_info_body: "The economic cost of opioid use disorder and fatal overdose was estimated based on the prevalence of opioid use disorder and the counts of fatal opioid overdose. The prevalence of opioid use disorder was obtained from the Substance Abuse & Mental Health Data Archive (https://www.datafiles.samhsa.gov/) and the counts of fatal opioid overdose from the CDC WONDER (https://wonder.cdc.gov/). Find more information about the data in this.\n\n-  Opioid use disorder costs include health care costs, substance use treatment costs, criminal justice costs, lost productivity, and value of reduced quality of life. \n\n-  Fatal overdose costs include health care costs, lost productivity, and value of statistical life lost. \n\n-  Total costs are the sum of opioid use disorder costs and fatal overdose costs. Per capita total costs, per capita opioid use disorder costs, and per capita fatal overdose costs are total costs, opioid use disorder costs, and fatal overdose costs divided by population, respectively. \n\nMaps are available for the 3 per capita types of costs by quartiles.",

  state_export_title_total: "Per Capita Economic Cost of Opioid Use Disorder and Fatal Overdose by State, 2017",
  state_export_title_nonfatal: "Per Capita Economic Cost of Opioid Use Disorder by State, 2017",
  state_export_title_fatal: "Per Capita Economic Cost of Opioid Fatal Overdose by State, 2017",

  county_title_total: "Economic Cost by County: Total Costs",
  county_title_nonfatal: "Economic Cost by County: Opioid Use Disorder Costs",
  county_title_fatal: "Economic Cost by County: Fatal Overdose Costs",
  county_description: "The dashboard presents the economic cost of opioid use disorder and fatal overdose at the county level in 2017. Estimates of the economic cost can help decision makers, clinicians, public health professionals, researchers, and other stakeholders to better understand the magnitude and distribution of opioid use disorder and fatal overdose costs in counties of the U.S.",
  county_info_title: "Data Sources and Other Information",
  county_info_body: "The economic cost of opioid use disorder and fatal overdose was estimated based on the prevalence of opioid use disorder and the count of fatal opioid overdose. The prevalence of opioid use disorder was estimated from the National Survey on Drug Use and Health and the counts of fatal opioid overdose was extracted from the National Vital Statistics System. Find more information about the data in this study [Placeholder for cost of opioid paper]. \n\n-  Opioid use disorder costs include health care costs, substance use treatment costs, criminal justice costs, lost productivity, and value of reduced quality of life. \n\n-  Fatal overdose costs include health care costs, lost productivity, and value of statistical life lost. \n\n-  Total costs are the sum of opioid use disorder costs and fatal overdose costs. Per capita total costs, per capita opioid use disorder costs, and per capita fatal overdose costs are total costs, opioid use disorder costs, and fatal overdose costs divided by population, respectively. \n\nMaps are available for the 3 per capita types of costs by quartiles.",
  county_map_prompt: "Select a state to view county level data",

  county_export_title_total: "Per Capita Economic Cost of Opioid Use Disorder and Fatal Overdose by County, 2017",
  county_export_title_nonfatal: "Per Capita Economic Cost of Opioid Use Disorder by County, 2017",
  county_export_title_fatal: "Per Capita Economic Cost of Opioid Fatal Overdose by County, 2017",

  summary_title: "Economic Cost Summary",
  summary_description: "The summary table presents the economic cost of opioid use disorder and fatal overdose at the state level in 2017. Estimates of the economic cost can help decision makers, clinicians, public health professionals, researchers, and other stakeholders better understand the magnitude and distribution of opioid use disorder and fatal overdose costs in individual states of the U.S.",
  summary_total_table: "Fatal and Nonfatal Opioid Use Disorder",
  summary_fatal_table: "Opioid Use Disorder (Nonfatal)",
  summary_nonfatal_table: "Fatal Overdose Mortality",

  footnote_total: "$: 2017 Dollars; M: Millions; B: Billions; FO: Fatal Overdose; OUD: Opioid Use Disorder",
  footnote_nonfatal: "$: 2017 Dollars; M: Millions; B: Billions; SU: Substance Use; QOL: Quality of Life; OUD: Opioid Use Disorder",
  footnote_fatal: "$: 2017 Dollars; M: Millions; B: Billions; SU: Substance Use; SLL: Statistical Life Lost; FO: Fatal Overdose",

  state_export_footnote: "The prevalence of opioid use disorder was obtained from the Substance Abuse & Mental Health Data Archive, the count of fatal opioid overdose from the CDC WONDER, and the cost estimates from \"The Economic Cost of Opioid Use Disorder and Fatal Opioid Overdose in the United States, 2017\" and \"The State-Level Economic Cost of Opioid Use Disorder and Fatal Opioid Overdose in the United States, 2017\". " +
    "Substance Abuse & Mental Health Data Archive: https://www.datafiles.samhsa.gov/ CDC WONDER: https://wonder.cdc.gov/ ",

  county_export_footnote: "The prevalence of opioid use disorder was obtained from the Substance Abuse & Mental Health Data Archive, the count of fatal opioid overdose from the CDC WONDER, and the cost estimates from \"The State-Level Economic Cost of Opioid Use Disorder and Fatal Opioid Overdose in the United States, 2017\" and \"The County-Level Economic Cost of Opioid Use Disorder and Fatal Opioid Overdose in the United States, 2017\". " +
    "Substance Abuse & Mental Health Data Archive: https://www.datafiles.samhsa.gov/ CDC WONDER: https://wonder.cdc.gov/",

  footnote_suppression: "*12 states data were suppressed because they did not meet requirements for adequate drug specificity in 2017. See \"Drug and Opioid-Involved Overdose Deaths — United States, 2017–2018\" (https://www.cdc.gov/mmwr/volumes/69/wr/pdfs/mm6911a4-H.pdf )",

  footnote_suppression_short: "*12 states data were suppressed because they did not meet requirements for adequate drug specificity in 2017.",

  cost_type: "Cost",
  cost_total: "Total Costs",
  cost_fatal: "Fatal Overdose Costs",
  cost_nonfatal: "Opioid Use Disorder Costs",

  display_type: "Display as",
  display_chart: "Chart",
  display_table: "Table",

  summary_short: "Summary",
  summary_detail: "Detail",

  summary_title_total: "Fatal and Nonfatal Opioid Use Disorder",
  summary_title_fatal: "Fatal Overdose Mortality",
  summary_title_nonfatal: "Opioid Use Disorder (Nonfatal)",

  location: "Location",
  united_states: "United States",

  selected_indicator: "[Selected]",
  comparison_indicator: "[Comparison]",
  selected_state: "Selected State",
  comparison_state: "Comparison State",
  selected_county: "Selected County",
  comparison_county: "Comparison County",

  selected_prompt: "Click map for quick selection",
  comparison_prompt: "Ctrl-Click map for quick comparison",

  modal_close: "Close",
  more_information: "More Information",
  user_guide: "View User Guide",

  suppressed_data: "Suppressed Data",
  suppressed_data_abbr: "---",

  table_expand: "Show More",
  table_collapse: "Show Less",

  form_submit: "Submit",
  form_reset: "Reset",
  form_none: "None",

  export_data: "Export Data",
  export_image: "Export Image",
  export_vector: "Export SVG",

  accessible_map_description: "A map with locations colored to match data classifications. For an accessible version of this data, change the 'Display As' control to the 'Table' option.",
  accessible_selector_description: "A map used for selecting states. Use the 'Selected State:' control for an accessible version of this action."
}

export const VARIABLE_LABELS_SHORT = {
  Location: 'Location',
  State: 'State',
  StateID: 'State Code',
  County: 'County',
  CountyID: 'County Code',
  OUD_Total: 'OUD Prevalence 2017',
  OUDC_HealthCare: 'Health Care',
  OUDC_Treatment: 'SU Treatment',
  OUDC_CriminalJustice: 'Criminal Justice',
  OUDC_LostProductivity: 'Lost Productivity',
  OUDC_RQL: 'Reduced QOL',
  OUDC_Total: 'Total OUD Costs',
  OUDC_TotalPercent: 'OUD % of Total Costs',
  OUDC_Total_quartile: 'Total OUD Costs Quartile',
  OUDC_perCapita: 'Per Capita OUD Costs',
  OUDC_perCapita_quartile: 'Per Capita OUD Costs Quartile',
  FOD_Total: 'FO Deaths 2017',
  FODC_HealthCare: 'Health Care',
  FODC_LostProductivity: 'Lost Productivity',
  FODC_VSL: 'Value of SLL',
  FODC_Total: 'Total FO Costs',
  FODC_TotalPercent: 'FO % of Total Costs',
  FODC_Total_quartile: 'Total FO Costs Quartile',
  FODC_perCapita: 'Per Capita FO Costs',
  FODC_perCapita_quartile: 'Per Capita FO Costs Quartile',
  TotalCost: 'Total Costs',
  TotalCost_quartile: 'Total Costs Quartile',
  Cost_perCapita: 'Per Capita Total Costs',
  Cost_perCapita_quartile: 'Per Capita Total Costs Quartile',
  POP: 'Population',
};

export const VARIABLE_LABELS_LONG = {
  Location: 'Location',
  State: 'State',
  StateID: 'State Code',
  County: 'County',
  CountyID: 'County Code',
  OUDC: 'Opioid Use Disorder Costs',
  OUD_Total: 'Opioid Use Disorder Prevalence',
  OUDC_HealthCare: 'OUD Health Care',
  OUDC_Treatment: 'OUD Substance Use Treatment',
  OUDC_CriminalJustice: 'OUD Criminal justice',
  OUDC_LostProductivity: 'OUD Lost productivity',
  OUDC_RQL: 'OUD Reduced Quality of Life',
  OUDC_Total: 'Total OUD Costs',
  OUDC_TotalPercent: 'OUD Percentage of Total Costs',
  OUDC_Total_quartile: 'Total OUD Costs Quartile',
  OUDC_perCapita: 'Per Capita Opioid Use Disorder Costs',
  OUDC_perCapita_quartile: 'Per Capita OUD Costs Quartile',
  FODC: 'Fatal Overdose Death Costs ',
  FOD_Total: 'Fatal Overdose Deaths',
  FODC_HealthCare: 'FO Health care',
  FODC_LostProductivity: 'FO Lost productivity',
  FODC_VSL: 'FO Value of Statistical Life Lost',
  FODC_Total: 'Total Fatal Overdose Costs',
  FODC_TotalPercent: 'FO Percentage of Total Costs',
  FODC_Total_quartile: 'Total FO Costs Quartile',
  FODC_perCapita: 'Per Capita Fatal Overdose Costs',
  FODC_perCapita_quartile: 'Per Capita FO Costs Quartile',
  TotalCost: 'Total Costs',
  TotalCost_quartile: 'Total Costs Quartile',
  Cost_perCapita: 'Per Capita Total Costs',
  Cost_perCapita_quartile: 'Per Capita Total Costs Quartile',
  POP: 'Population',
};


